import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBell } from 'react-icons/fa';
import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
Chart.register(ArcElement, Tooltip, Legend);

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [roleCounts, setRoleCounts] = useState({ worker: 0, admin: 0, business: 0 });

  // Mock de usuario admin
  const adminData = {
    firstName: 'Jose',
    status: 'pending',
  };

  // Mock de notificaciones
  const notifications = [
    {
      id: 1,
      title: 'Nueva solicitud de registro',
      shortDescription: 'Hay 3 usuarios esperando aprobación.',
      date: new Date().toISOString(),
    },
    {
      id: 2,
      title: 'Reporte semanal',
      shortDescription: 'El reporte semanal ya está disponible.',
      date: new Date().toISOString(),
    },
  ];

  // Simular datos de métricas
  const metrics = [
    { label: t('adminDashboard.totalUsers'), value: 1200 },
    { label: t('adminDashboard.activeUsers'), value: 980 },
    { label: t('adminDashboard.pendingUsers'), value: 220 },
  ];

  // Datos mock para la dona de roles
  const roleData = {
    labels: [
      t('userRoles.worker'),
      t('userRoles.admin'),
      t('userRoles.business')
    ],
    datasets: [
      {
        data: [roleCounts.worker, roleCounts.admin, roleCounts.business],
        backgroundColor: [
          '#D50B3F', // Worker
          '#1d1d1b', // Admin
          '#ffb300'  // Business
        ],
        borderWidth: 2,
        borderColor: '#fff',
        hoverOffset: 8,
      },
    ],
  };

  const roleOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          font: { family: 'Montserrat', size: 14 },
        },
      },
    },
    cutout: '70%',
    responsive: true,
    maintainAspectRatio: false,
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Obtener datos reales de roles de usuarios
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        console.log('[AdminDashboard] Usuario autenticado:', currentUser?.uid, currentUser?.email);
        const db = getFirestore();
        const usersSnapshot = await getDocs(collection(db, 'users'));
        let worker = 0, admin = 0, business = 0;
        const allUsers: any[] = [];
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          allUsers.push({ id: doc.id, ...data });
          const role = data.role;
          if (role === 'worker' || role === 'user') worker++;
          else if (role === 'admin') admin++;
          else if (role === 'business') business++;
        });
        console.log('[AdminDashboard] Usuarios obtenidos:', allUsers);
        console.log('[AdminDashboard] Conteo de roles:', { worker, admin, business });
        setRoleCounts({ worker, admin, business });
      } catch (error) {
        console.error('[AdminDashboard] Error al obtener usuarios:', error);
      }
    };
    fetchUserRoles();
  }, []);

  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.getMonth();
    const year = date.getFullYear();
    const monthsEN = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthsES = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return i18n.language === 'es'
      ? `${day}-${monthsES[month]}-${year}`
      : `${monthsEN[month]}-${day}-${year}`;
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  return (
    <div className="p-8 w-full min-h-screen bg-gray-100 font-['Montserrat'] relative overflow-hidden">
      <header className="flex justify-between items-center mb-12 p-8 bg-gradient-to-br from-white to-gray-50 rounded-[20px_60px_30px_20px] shadow-lg w-full relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-1.5 before:bg-gradient-to-r before:from-red-600 before:to-pink-500 before:rounded-b-3xl before:z-10">
        <div className="relative flex items-center mr-6">
          <button 
            onClick={() => setPopoverOpen((open) => !open)} 
            aria-label={t('workerDashboard.notifications')}
            className="bg-transparent border-none cursor-pointer text-2xl text-red-600 mr-3 flex items-center transition-colors duration-200 hover:text-pink-500"
          >
            <FaBell />
          </button>
          {popoverOpen && (
            <div className="fixed top-20 left-10 min-w-80 bg-white rounded-[16px_32px_16px_32px] shadow-2xl z-50 p-5 font-['Montserrat']">
              <button 
                onClick={() => setPopoverOpen(false)} 
                aria-label={t('workerDashboard.closeNotifications')}
                className="absolute top-3 right-4 bg-transparent border-none text-xl text-gray-500 cursor-pointer opacity-70 transition-opacity duration-200 hover:opacity-100 hover:text-red-600"
              >
                ×
              </button>
              <div className="font-bold text-lg mb-3 text-red-600">
                {t('workerDashboard.notifications')}
              </div>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div key={notification.id} className="p-3 rounded-lg mb-2 bg-gray-50 text-sm text-gray-800 shadow-sm">
                    <strong>{notification.title}</strong>
                    <div className="text-xs mt-1 mb-0.5 text-gray-500">
                      {new Date(notification.date).toLocaleDateString(i18n.language)}
                    </div>
                    <div>{notification.shortDescription}</div>
                  </div>
                ))
              ) : (
                <div className="p-3 rounded-lg mb-2 bg-gray-50 text-sm text-gray-800 shadow-sm">
                  {t('workerDashboard.noNotifications')}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-center gap-2 flex-1 text-center">
          <div className="text-xl font-medium text-gray-800 leading-tight">
            {t('adminDashboard.welcome', { name: adminData.firstName })}
          </div>
          <div className="text-4xl font-bold text-red-600 leading-tight">
            {formatTime(currentTime)}
          </div>
          <div className="text-xl font-medium text-gray-800 leading-tight">
            {formatDate(currentTime)}
          </div>
        </div>
        <span className="px-6 py-3 rounded-full bg-gradient-to-br from-red-600 to-pink-500 text-white font-semibold shadow-lg transform -translate-y-0.5 transition-transform duration-300">
          Status: {adminData.status.charAt(0).toUpperCase() + adminData.status.slice(1)}
        </span>
      </header>

      <div className="flex gap-8 mt-8 flex-wrap justify-center">
        {metrics.map((metric, idx) => (
          <div key={idx} className="bg-white rounded-2xl shadow-sm p-9 min-w-56 flex flex-col items-center">
            <div className="text-4xl font-bold text-red-600">
              {metric.value}
            </div>
            <div className="text-lg text-gray-700 font-medium mt-2">
              {metric.label}
            </div>
          </div>
        ))}
      </div>
      
      <div className="w-85 mx-auto mt-10 bg-white rounded-3xl shadow-sm p-8 flex flex-col items-center">
        <h3 className="mb-5 text-gray-700 font-bold text-lg">
          {t('adminDashboard.userRoles')}
        </h3>
        <div className="w-55 h-55">
          <Doughnut data={roleData} options={roleOptions} />
        </div>
      </div>
    </div>
  );
} 